import { useState, useEffect } from 'react';
import { Check, Eye, Plus, Edit2, Trash2, AlertCircle } from '../../icons/wp-icons';
import { getBenutzerById, updatePermissions } from '../../api/benutzerApi';

// Alle Module mit Labels
const MODULES = [
  { key: 'DASHBOARD', label: 'Dashboard', hasCreate: false, hasEdit: false, hasDelete: false },
  { key: 'KUNDEN', label: 'Kunden', hasCreate: true, hasEdit: true, hasDelete: true },
  { key: 'ANFRAGEN', label: 'Anfragen', hasCreate: true, hasEdit: true, hasDelete: true },
  { key: 'TERMINE', label: 'Kalender', hasCreate: true, hasEdit: true, hasDelete: true },
  { key: 'PLANTAFEL', label: 'Plantafel', hasCreate: false, hasEdit: true, hasDelete: false, tooltip: 'Dispo: Termine per Drag&Drop Fahrzeugen/Werkzeugen zuweisen' },
  { key: 'PROJEKTE', label: 'Projekte', hasCreate: true, hasEdit: true, hasDelete: true },
  { key: 'RECHNUNGEN', label: 'Rechnungen', hasCreate: true, hasEdit: true, hasDelete: true },
  { key: 'KOSTENVORANSCHLAEGE', label: 'Kostenvoranschläge', hasCreate: true, hasEdit: true, hasDelete: true },
  { key: 'ZEITERFASSUNG', label: 'Zeiterfassung', hasCreate: true, hasEdit: true, hasDelete: true },
  { key: 'KATALOG', label: 'Katalog', hasCreate: true, hasEdit: true, hasDelete: true },
  { key: 'EINSTELLUNGEN', label: 'Einstellungen', hasCreate: false, hasEdit: true, hasDelete: false },
  { key: 'KI_ASSISTENT', label: 'KI-Assistent', hasCreate: false, hasEdit: false, hasDelete: false },
  // KI-Benachrichtigungen: canView = Benachrichtigungen sehen, canEdit = Bestätigen/Ablehnen von Terminen etc.
  { key: 'KI_BENACHRICHTIGUNGEN', label: 'KI-Benachrichtigungen', hasCreate: false, hasEdit: true, hasDelete: false, tooltip: 'Benachrichtigungen vom KI-Email- und Telefonassistenten empfangen und bearbeiten' }
];

// Standard-Berechtigungen für Mitarbeiter
const DEFAULT_PERMISSIONS = {
  DASHBOARD: { canView: true, canCreate: false, canEdit: false, canDelete: false },
  KUNDEN: { canView: true, canCreate: true, canEdit: true, canDelete: false },
  ANFRAGEN: { canView: true, canCreate: true, canEdit: true, canDelete: false },
  TERMINE: { canView: true, canCreate: true, canEdit: true, canDelete: false },
  PROJEKTE: { canView: true, canCreate: true, canEdit: true, canDelete: false },
  RECHNUNGEN: { canView: true, canCreate: false, canEdit: false, canDelete: false },
  KOSTENVORANSCHLAEGE: { canView: true, canCreate: true, canEdit: true, canDelete: false },
  ZEITERFASSUNG: { canView: true, canCreate: true, canEdit: true, canDelete: false },
  KATALOG: { canView: true, canCreate: false, canEdit: false, canDelete: false },
  EINSTELLUNGEN: { canView: false, canCreate: false, canEdit: false, canDelete: false },
  KI_ASSISTENT: { canView: true, canCreate: false, canEdit: false, canDelete: false },
  KI_BENACHRICHTIGUNGEN: { canView: false, canCreate: false, canEdit: false, canDelete: false }
};

function BerechtigungsMatrix({ benutzerId, benutzerRolle, onSaved, onCancel }) {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPermissions();
  }, [benutzerId]);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const benutzer = await getBenutzerById(benutzerId);

      // Berechtigungen initialisieren mit Defaults wo nicht vorhanden
      const loadedPerms = {};
      MODULES.forEach(mod => {
        loadedPerms[mod.key] = benutzer.berechtigungen?.[mod.key] || {
          canView: false,
          canCreate: false,
          canEdit: false,
          canDelete: false
        };
      });

      setPermissions(loadedPerms);
    } catch (err) {
      console.error('Fehler beim Laden:', err);
      setError('Berechtigungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (modul, action) => {
    setPermissions(prev => ({
      ...prev,
      [modul]: {
        ...prev[modul],
        [action]: !prev[modul]?.[action]
      }
    }));
  };

  const handleFullAccess = () => {
    const fullAccess = {};
    MODULES.forEach(mod => {
      fullAccess[mod.key] = {
        canView: true,
        canCreate: mod.hasCreate,
        canEdit: mod.hasEdit,
        canDelete: mod.hasDelete
      };
    });
    setPermissions(fullAccess);
  };

  const handleReadOnly = () => {
    const readOnly = {};
    MODULES.forEach(mod => {
      readOnly[mod.key] = {
        canView: true,
        canCreate: false,
        canEdit: false,
        canDelete: false
      };
    });
    setPermissions(readOnly);
  };

  const handleReset = () => {
    setPermissions({ ...DEFAULT_PERMISSIONS });
  };

  const handleClearAll = () => {
    const cleared = {};
    MODULES.forEach(mod => {
      cleared[mod.key] = {
        canView: false,
        canCreate: false,
        canEdit: false,
        canDelete: false
      };
    });
    setPermissions(cleared);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      await updatePermissions(benutzerId, permissions);
      onSaved();
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Admins haben immer Vollzugriff
  if (benutzerRolle === 'admin') {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-purple-600" />
          <p className="text-purple-700">
            <strong>Admin-Konto:</strong> Administratoren haben automatisch Vollzugriff auf alle Module.
            Die Berechtigungen können nicht eingeschränkt werden.
          </p>
        </div>
        <button
          onClick={onCancel}
          className="w-full px-4 py-2 border border-border rounded-button hover:bg-gray-50 transition"
        >
          Schließen
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Schnellauswahl-Buttons */}
      <div className="flex flex-wrap gap-2 pb-4 border-b border-border">
        <button
          onClick={handleFullAccess}
          className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition"
        >
          Vollzugriff
        </button>
        <button
          onClick={handleReadOnly}
          className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
        >
          Nur Lesen
        </button>
        <button
          onClick={handleReset}
          className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
        >
          Standard
        </button>
        <button
          onClick={handleClearAll}
          className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
        >
          Alle entfernen
        </button>
      </div>

      {/* Berechtigungs-Matrix */}
      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <thead>
            <tr className="border-b border-border bg-gray-50">
              <th className="text-left py-3 px-4 font-medium w-40">Modul</th>
              <th className="py-3 px-2 font-medium w-24">
                <div className="flex flex-col items-center justify-center">
                  <Eye className="w-4 h-4 mb-1" />
                  <span className="text-xs">Ansehen</span>
                </div>
              </th>
              <th className="py-3 px-2 font-medium w-24">
                <div className="flex flex-col items-center justify-center">
                  <Plus className="w-4 h-4 mb-1" />
                  <span className="text-xs">Erstellen</span>
                </div>
              </th>
              <th className="py-3 px-2 font-medium w-24">
                <div className="flex flex-col items-center justify-center">
                  <Edit2 className="w-4 h-4 mb-1" />
                  <span className="text-xs">Bearbeiten</span>
                </div>
              </th>
              <th className="py-3 px-2 font-medium w-24">
                <div className="flex flex-col items-center justify-center">
                  <Trash2 className="w-4 h-4 mb-1" />
                  <span className="text-xs">Löschen</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {MODULES.map((mod) => (
              <tr key={mod.key} className="border-b border-border hover:bg-gray-50">
                <td className="py-3 px-4 font-medium" title={mod.tooltip || ''}>
                  {mod.label}
                </td>
                <td className="py-3 px-2">
                  <div className="flex justify-center">
                    <Checkbox
                      checked={permissions[mod.key]?.canView}
                      onChange={() => handleToggle(mod.key, 'canView')}
                    />
                  </div>
                </td>
                <td className="py-3 px-2">
                  <div className="flex justify-center">
                    {mod.hasCreate ? (
                      <Checkbox
                        checked={permissions[mod.key]?.canCreate}
                        onChange={() => handleToggle(mod.key, 'canCreate')}
                      />
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </div>
                </td>
                <td className="py-3 px-2">
                  <div className="flex justify-center">
                    {mod.hasEdit ? (
                      <Checkbox
                        checked={permissions[mod.key]?.canEdit}
                        onChange={() => handleToggle(mod.key, 'canEdit')}
                      />
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </div>
                </td>
                <td className="py-3 px-2">
                  <div className="flex justify-center">
                    {mod.hasDelete ? (
                      <Checkbox
                        checked={permissions[mod.key]?.canDelete}
                        onChange={() => handleToggle(mod.key, 'canDelete')}
                      />
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Buttons */}
      <div className="flex gap-3 pt-4 border-t border-border">
        <button
          onClick={onCancel}
          className="flex-1 px-4 py-2 border border-border rounded-button hover:bg-gray-50 transition"
        >
          Abbrechen
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 px-4 py-2 bg-primary text-white rounded-button hover:bg-primary-dark transition disabled:opacity-50"
        >
          {saving ? 'Speichern...' : 'Berechtigungen speichern'}
        </button>
      </div>
    </div>
  );
}

// Checkbox-Komponente
function Checkbox({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`w-6 h-6 rounded border-2 flex items-center justify-center transition ${
        checked
          ? 'bg-primary border-primary text-white'
          : 'border-gray-300 hover:border-primary'
      }`}
    >
      {checked && <Check className="w-4 h-4" />}
    </button>
  );
}

export default BerechtigungsMatrix;
